import { injectMapDataServices } from '../definitions/map-data.service';
import { inject } from '@angular/core';
import type { FilterModel } from '@ui/shared/util-filter';
import { FilterConfigToken, type FilterValue } from '@ui/shared/util-filter';
import type { Observable } from 'rxjs';
import { map, zip } from 'rxjs';
import { BaseMapDataStateService, type MapDataResult } from './base-map-data-state.service';

type Filter<Key extends string> = Partial<Record<Key, FilterValue>>;

/**
 * a map data service that uses injected configurations for filter and data
 */
export class InjectableMapDataStateService<Item extends { title: string }, FilterKey extends string>
    extends BaseMapDataStateService<Item> {
    protected _dataServices$ = injectMapDataServices<Item, Filter<FilterKey>>();
    protected _filters = inject(FilterConfigToken);

    protected _getFilter(filters: Map<string, FilterModel>): Filter<FilterKey> {
        return [
            ...this._filters.map(config => config.filterModel),
        ]
            .reduce<Filter<FilterKey>>((state, filter) => ({
                ...state,
                [filter.key]: filter.isMultiple ? filters.get(filter.key)?.getValues() : filters.get(filter.key)?.getValue(),
            }), {});
    }

    public items$({ filters }: { filters: Map<string, FilterModel> }): Observable<MapDataResult<Item>> {
        const filterValues = this._getFilter(filters);

        return this.getData$(
            zip(this._dataServices$.map(service => service.getData$(filterValues)))
                .pipe(
                    map(data => data
                        .flat()
                        .sort((a, b) => (a.title.toLowerCase() === b.title.toLowerCase()
                                    ? 0
                                    : a.title.toLowerCase() > b.title.toLowerCase()
                                        ? 1
                                        : -1))),
                ),
        );
    }
}
